import type { Priority } from '@/types';
import {
  DATE_EXPLICIT_REGEX,
  DATE_RELATIVE_REGEX,
  DATE_RELATIVE_WEEK_REGEX,
  DATE_WEEKDAY_REGEX,
  DURATION_REGEX,
  DURATION_UNIT_MIN,
  MONTH_INDEX,
  PRIORITY_BANG_REGEX,
  PRIORITY_CRITICAL_REGEX,
  PRIORITY_HIGH_REGEX,
  PRIORITY_LOW_REGEX,
  PRIORITY_MEDIUM_REGEX,
  PRIORITY_RANK,
  TAG_REGEX,
  TIME_AT_PREFIX,
  TIME_REGEX_24H,
  TIME_REGEX_AMPM,
  TIME_REGEX_NAMED,
  WEEKDAY_INDEX,
} from './tokens';

export interface ParsedTask {
  title: string;
  date: Date | null;
  timeMinutes: number | null;
  durationMinutes: number | null;
  priority: Priority | null;
  tags: string[];
}

/**
 * Cursor-style extraction: working string gets tokens stripped as we match.
 * The final `title` is the working string trimmed and collapsed.
 */
class Cursor {
  constructor(public text: string) {}

  /** Remove the first match of a non-global regex; return matched values. */
  consume(re: RegExp): RegExpMatchArray[] {
    const matches: RegExpMatchArray[] = [];
    if (re.flags.includes('g')) {
      this.text = this.text.replace(re, (fullMatch, ...rest) => {
        // rest: [...captureGroups, offset, string]
        const offset = rest[rest.length - 2] as number;
        const source = rest[rest.length - 1] as string;
        const captureGroups = rest.slice(0, -2) as string[];
        const m = [fullMatch, ...captureGroups] as RegExpMatchArray;
        m.index = offset;
        m.input = source;
        matches.push(m);
        return ' ';
      });
    } else {
      const m = this.text.match(re);
      if (m) {
        matches.push(m);
        this.text = this.text.replace(re, ' ');
      }
    }
    return matches;
  }

  finalTitle(): string {
    return this.text.replace(/\s+/g, ' ').trim();
  }
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function nextWeekday(now: Date, targetDow: number): Date {
  const today = startOfLocalDay(now);
  const todayDow = today.getDay();
  let diff = (targetDow - todayDow + 7) % 7;
  if (diff === 0) diff = 7; // "friday" from a Friday → next Friday (same weekday)
  const out = new Date(today);
  out.setDate(out.getDate() + diff);
  return out;
}

function nextMonthDate(now: Date, monthIdx: number, day: number): Date {
  const candidate = new Date(now.getFullYear(), monthIdx, day);
  candidate.setHours(0, 0, 0, 0);
  if (candidate.getTime() <= startOfLocalDay(now).getTime()) {
    candidate.setFullYear(candidate.getFullYear() + 1);
  }
  return candidate;
}

function extractTags(cur: Cursor): string[] {
  const tags: string[] = [];
  cur.text = cur.text.replace(TAG_REGEX, (match) => {
    tags.push(match.slice(1).toLowerCase());
    return ' ';
  });
  return tags;
}

function extractDuration(cur: Cursor): number | null {
  const m = cur.consume(DURATION_REGEX)[0];
  if (!m) return null;
  const value = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  return value * (DURATION_UNIT_MIN[unit] ?? 1);
}

function extractTime(cur: Cursor): number | null {
  // Strip optional "at " prefix before time matching so it doesn't end up in title.
  // First try named ("noon", "midnight") — most specific.
  const named = cur.consume(TIME_REGEX_NAMED)[0];
  if (named) {
    cur.consume(TIME_AT_PREFIX);
    return named[1].toLowerCase() === 'noon' ? 720 : 0;
  }

  // Then am/pm (avoids 24h regex matching "2pm" partially).
  const ampm = cur.consume(TIME_REGEX_AMPM)[0];
  if (ampm) {
    cur.consume(TIME_AT_PREFIX);
    let h = parseInt(ampm[1], 10);
    const min = ampm[2] ? parseInt(ampm[2], 10) : 0;
    const suffix = ampm[3].toLowerCase();
    if (suffix === 'am' && h === 12) h = 0;
    if (suffix === 'pm' && h !== 12) h += 12;
    return h * 60 + min;
  }

  // Then 24h — requires colon (HH:MM format, no bare bare numbers).
  const h24 = cur.consume(TIME_REGEX_24H)[0];
  if (h24) {
    cur.consume(TIME_AT_PREFIX);
    const h = parseInt(h24[1], 10);
    const min = parseInt(h24[2], 10);
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }

  return null;
}

function extractDate(cur: Cursor, now: Date): Date | null {
  // Order: relative-week (next week), then explicit (jan 15), then weekday, then relative (today/tomorrow).
  const relWeek = cur.consume(DATE_RELATIVE_WEEK_REGEX)[0];
  if (relWeek) {
    const phrase = relWeek[1].toLowerCase();
    if (phrase === 'next week') return nextWeekday(now, 1); // next Monday
    if (phrase === 'this week') {
      // Current Monday (or today if today is Monday).
      const today = startOfLocalDay(now);
      const dow = today.getDay();
      const diff = dow === 0 ? -6 : 1 - dow; // back to Monday
      const out = new Date(today);
      out.setDate(out.getDate() + diff);
      return out;
    }
  }

  const explicit = cur.consume(DATE_EXPLICIT_REGEX)[0];
  if (explicit) {
    const monthIdx = MONTH_INDEX[explicit[1].toLowerCase()];
    const day = parseInt(explicit[2], 10);
    return nextMonthDate(now, monthIdx, day);
  }

  const weekday = cur.consume(DATE_WEEKDAY_REGEX)[0];
  if (weekday) {
    const dow = WEEKDAY_INDEX[weekday[1].toLowerCase()];
    return nextWeekday(now, dow);
  }

  const relative = cur.consume(DATE_RELATIVE_REGEX)[0];
  if (relative) {
    const phrase = relative[1].toLowerCase();
    if (phrase === 'today' || phrase === 'tonight') return startOfLocalDay(now);
    if (phrase === 'tomorrow' || phrase === 'tmrw') {
      const d = startOfLocalDay(now);
      d.setDate(d.getDate() + 1);
      return d;
    }
  }

  return null;
}

function extractPriority(cur: Cursor): Priority | null {
  const candidates: Priority[] = [];

  // "!" at start (anchored). Must run BEFORE other priority regexes because it strips from start.
  if (PRIORITY_BANG_REGEX.test(cur.text)) {
    cur.text = cur.text.replace(PRIORITY_BANG_REGEX, ' ');
    candidates.push('high');
  }
  if (cur.consume(PRIORITY_CRITICAL_REGEX)[0]) candidates.push('critical');
  if (cur.consume(PRIORITY_HIGH_REGEX)[0]) candidates.push('high');
  if (cur.consume(PRIORITY_LOW_REGEX)[0]) candidates.push('low');
  if (cur.consume(PRIORITY_MEDIUM_REGEX)[0]) candidates.push('medium');

  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (PRIORITY_RANK[a] >= PRIORITY_RANK[b] ? a : b));
}

export function parseTaskInput(raw: string, now: Date = new Date()): ParsedTask {
  const cur = new Cursor(raw);

  // Extraction order matters — tags first (rare confusion), then duration (greedy units),
  // then time, then date, then priority. Title is whatever's left.
  const tags = extractTags(cur);
  const durationMinutes = extractDuration(cur);
  const timeMinutes = extractTime(cur);
  const date = extractDate(cur, now);
  const priority = extractPriority(cur);

  return {
    title: cur.finalTitle(),
    date,
    timeMinutes,
    durationMinutes,
    priority,
    tags,
  };
}
